import { Amount, asAmount, BondPriceNominal, OrderDirection, TimeInForce, Yield } from "@goodfill/domain-properties";
import {
  BalanceAccountAmountComponent,
  BondPriceComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { Flex, Icon, InputCheckbox, Label, Span } from "@goodfill/ui-components";
import { DateTime } from "luxon";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserAuthDataBalanceAccount } from "../../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { isFormValid } from "../../../../form/formValidation";
import { FormValidationComponent } from "../../../../form/FormValidationComponent";
import { route } from "../../../../routes";
import { OrderFormState } from "../state/OrderFormState";
import { validateCreateOrderState } from "../state/validateCreateOrderState";
import { CreateOrderActions } from "./CreateOrderActions";
import { CreateOrderExpirationDateInput } from "./CreateOrderExpirationDateInput";
import { CreateOrderFaceAmountInput } from "./CreateOrderFaceAmountInput";
import { CreateOrderStatistics } from "./CreateOrderStatistics";
import { CustomOrderPriceVsYield } from "./CustomOrderPriceVsYield";
import "./OrderForm.scss";
import { Link } from "../../../../ui-components/Link/Link";
import { useRecoilValue } from "recoil";
import { userDataState } from "../../../../state";
import { OrderValueInfoHorizontal } from "./OrderValueInfoHorizontal";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

interface Props {
  moneyBalanceAccount: UserAuthDataBalanceAccount;
  initState: OrderFormState;
  onSubmit: (state: OrderFormState) => void;
  isLoading: boolean;
  availableToSell: Amount | null;
}

export const OrderForm: React.FC<Props> = ({
  availableToSell,
  initState,
  moneyBalanceAccount,
  onSubmit,
  isLoading,
}) => {
  const userData = useRecoilValue(userDataState);

  if (userData === null) {
    return null;
  }

  const navigate = useNavigate();

  const [state, setState] = useState<OrderFormState>(initState);
  const [showOrderPreview, setShowOrderPreview] = useState(false);

  const pricedBond = state.i.pricedBond;

  const validation = validateCreateOrderState(state, {
    currentDate: DateTime.local(),
    availableBalance: moneyBalanceAccount.disponibleBalance,
  });

  const formIsValid = isFormValid(validation);

  const onClickCallToAction = async () => {
    if (showOrderPreview) {
      onSubmit(state);
    } else {
      setShowOrderPreview(true);
    }
  };

  const orderDirection = state.i.orderDirection;

  const prevailingMarketPrice = orderDirection === OrderDirection.Buy ? pricedBond.askPrice : pricedBond.bidPrice;

  const onClickModify = () => {
    setShowOrderPreview(false);
  };

  const onClickCancel = () => {
    navigate(route.dashboard());
  };

  const isReadOnly = isLoading || showOrderPreview;

  const onResetToMarketPrice = () => {
    setState((state) => state.resetToMarketPrice());
  };

  const onFaceAmountChange = (newFaceAmount: Amount) => {
    setState((state) => state.withFaceAmount(newFaceAmount));
  };

  const onPriceVsYieldChange = (value: "price" | "yield") => {
    setState((state) => state.withPriceVsYield(value));
  };

  const onCustomPriceChange = (newPrice: BondPriceNominal) => {
    setState((state) => state.withCustomLoadedPrice(newPrice, DateTime.local()));
  };

  const onCustomYieldChange = (newYield: Yield) => {
    setState((state) => state.withCustomLoadedYield(newYield, DateTime.local()));
  };

  const setExpirationDate = (value: DateTime) => {
    setState((state) => state.withExpirationDate(value));
  };

  const toggleIsAllOrNothing = () => {
    setState((state) => state.withIsAllOrNothing(!state.s.isAllOrNothing));
  };

  const isSubmitDisabled = !formIsValid;

  const currency = moneyBalanceAccount.currency;
  if (currency === null) {
    return <>ERROR: missing currency in moneyBalanceAccount</>;
  }

  return (
    <>
      <Flex direction="col" gap={4} className="custom-center-on-980">
        <Flex direction="col" verticalPadding={2}>
          <div className="info-details-container px-9 py-2 mb-7 mt-0-important flex justify-between cash-balance-container">
            <div className="flex">
              <div className="w-10 h-10 dollar-styles flex justify-center items-center">
                <Icon name={"dollar"} />
              </div>
              <Span className="cash-balance-fonts">Cash Balance</Span>
            </div>
            <div className="flex items-center font-semibold text-sm">
              <BalanceAccountAmountComponent value={moneyBalanceAccount.disponibleBalance} currency={currency} />
            </div>
          </div>
        </Flex>
        <div className="grid-cols-2 grid grid-cols-1-custom immediate-divider">
          <div>
            <CreateOrderFaceAmountInput
              disabled={isReadOnly}
              validation={validation.faceAmount}
              value={state.s.faceAmount.amount}
              onChange={onFaceAmountChange}
              state={state.s.timeInForce}
              CheckboxDiv={
                <>
                  {!(state.s.timeInForce === TimeInForce.Immediate) && !state.isEdit() ? (
                    <Flex direction="col" width={"w-full"}>
                      <Flex direction="rowCol" justify="start">
                        <Flex items="center" />
                        <Flex direction="row" items="center">
                          <InputCheckbox
                            id="isAllOrNothing"
                            checked={state.s.isAllOrNothing}
                            onChange={toggleIsAllOrNothing}
                            disabled={isReadOnly}
                            className="checkbox-styles"
                          />
                          <Label htmlFor="isAllOrNothing" className="all-or-nothing-fonts">
                            All or Nothing
                          </Label>
                        </Flex>
                      </Flex>
                    </Flex>
                  ) : null}
                </>
              }
            />
            <Flex direction="row" gap={2} verticalMargin={4} className="px-8 offer-priceOrYield-container">
              <OrderValueInfoHorizontal
                label={<>{state.isBuy() ? "Offer" : "Bid"} Price</>}
                tooltip="Current Post - Fee Offer Price"
              >
                <BondPriceComponent value={state.c.defaultLoadedPrice} />
              </OrderValueInfoHorizontal>

              <OrderValueInfoHorizontal
                label={<>{state.isBuy() ? "Offer" : "Bid"} Yield</>}
                tooltip="Current Post - Fee Offer Yield"
              >
                <YieldComponent value={state.c.defaultLoadedYield} />
              </OrderValueInfoHorizontal>
            </Flex>
            <Flex direction="row" className="px-8 offer-priceOrYield-container">
              <CustomOrderPriceVsYield
                disabled={isReadOnly}
                validation={[...validation.customLoadedPrice, ...validation.customLoadedYield]}
                priceVsYield={state.s.priceVsYield}
                setPriceVsYield={onPriceVsYieldChange}
                customPrice={state.s.customLoadedPrice}
                setCustomPrice={onCustomPriceChange}
                customYield={state.s.customLoadedYield}
                setCustomYield={onCustomYieldChange}
                ResetLink={
                  <>
                    {!isReadOnly && state.hasNonMarketPriceOrYield() ? (
                      <Flex direction="col" justify="end">
                        <Link onClick={onResetToMarketPrice} disabled={isReadOnly} className="reset-link-fonts">
                          <p className="mb-0-important"> Reset to Market Level</p>
                        </Link>
                      </Flex>
                    ) : null}
                  </>
                }
              />
            </Flex>
          </div>
          <Flex className="expiration-input-container pl-0.5 items-start">
            {!state.isImmediate() && state.s.expirationDate !== null ? (
              <div>
                <CreateOrderExpirationDateInput
                  timezone={TIMEZONE_NEY_YORK}
                  disabled={isReadOnly}
                  timeInForce={state.s.timeInForce}
                  priceVsYield={state.s.priceVsYield}
                  validation={validation.expirationDate}
                  value={state.s.expirationDate}
                  onChange={setExpirationDate}
                />
                <OrderValueInfoHorizontal
                  label={<>{state.isBuy() ? "Offer" : "Bid"} Yield on expiration date:</>}
                  tooltip="Current Post - Fee Offer Yield"
                >
                  <YieldComponent value={state.getYieldOnExpirationDate(state.s.expirationDate)} />
                </OrderValueInfoHorizontal>
              </div>
            ) : (
              <FormValidationComponent
                validation={validation.expirationDate}
                className="px-0-important expiration-input-error-boundary"
              />
            )}
          </Flex>
        </div>
      </Flex>

      <div className="horizontal-divider mx-2" />

      <CreateOrderStatistics
        showCalculatedInfo={true}
        currency={currency}
        accruedInterest={state.c.accruedInterest}
        tradeDate={state.s.timeInForce !== TimeInForce.CustomFuture ? state.i.tradeDate : null}
        settlementDate={state.s.timeInForce !== TimeInForce.CustomFuture ? state.i.settlementDate : null}
        settlementValue={state.settlementAmount}
        transactionCostAmount={state.c.transactionCostAmount}
        transactionCostRateUpfront={state.c.transactionCostRateUpfront}
        prevailingMarketPrice={prevailingMarketPrice}
        actualInvestment={asAmount(state.s.faceAmount.amount.mul(state.s.customLoadedPrice))}
      />

      <CreateOrderActions
        isCreatingOrder={isLoading}
        onClickModify={onClickModify}
        onClickCallToAction={onClickCallToAction}
        onClickCancel={onClickCancel}
        orderDirection={orderDirection}
        showOrderPreview={showOrderPreview}
        isDisabled={isSubmitDisabled}
      />
    </>
  );
};
